import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  DEFAULT_CUSTOM_VIEW_ID,
  groupColumns,
  resolveMatchingCollectionId,
} from "./columnCustomizationUtils";

const ACCENT = "#6759FF";
const ACCENT_SOFT = "rgba(103, 89, 255, 0.12)";
const PANEL_BG = "#F7F6FF";

const CollectionList = ({
  title,
  collections,
  activeId,
  onSelect,
  onRename,
  onDelete,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuCollection, setMenuCollection] = useState(null);

  if (!collections.length) return null;

  const handleOpenMenu = (event, collection) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuCollection(collection);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuCollection(null);
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: "#8A8699",
          px: 1.5,
          mb: 0.75,
        }}
      >
        {title}
      </Typography>
      <Stack spacing={0.5}>
        {collections.map((collection) => {
          const selected = collection.id === activeId;
          const canManage =
            collection.section === "custom" && !collection.isDefault;
          return (
            <Stack
              key={collection.id}
              direction="row"
              alignItems="center"
              onClick={() => onSelect(collection)}
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                cursor: "pointer",
                bgcolor: selected ? ACCENT_SOFT : "transparent",
                color: selected ? ACCENT : "#2F2B3A",
                fontWeight: selected ? 600 : 500,
                fontSize: 13,
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: selected ? ACCENT_SOFT : "rgba(47, 43, 58, 0.04)",
                },
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: "inherit",
                  color: "inherit",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  pr: canManage ? 0.5 : 0,
                }}
              >
                {collection.label}
              </Typography>
              {canManage && (
                <IconButton
                  size="small"
                  onClick={(event) => handleOpenMenu(event, collection)}
                  sx={{ p: 0.25, flexShrink: 0, color: "#8A8699" }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>
          );
        })}
      </Stack>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => {
            const collection = menuCollection;
            handleCloseMenu();
            if (collection) onRename?.(collection);
          }}
          sx={{ fontSize: 13, minHeight: 36 }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            const collection = menuCollection;
            handleCloseMenu();
            if (collection) onDelete?.(collection);
          }}
          sx={{ fontSize: 13, minHeight: 36, color: "#d32f2f" }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

const NamePromptDialog = ({
  open,
  title,
  label,
  initialValue = "",
  confirmLabel = "Save",
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [name, setName] = useState(initialValue);

  useEffect(() => {
    if (open) setName(initialValue || "");
  }, [open, initialValue]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={label}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && name.trim()) {
              onConfirm?.(name.trim());
            }
          }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!name.trim() || loading}
          onClick={() => onConfirm?.(name.trim())}
          sx={{
            textTransform: "none",
            bgcolor: ACCENT,
            "&:hover": { bgcolor: "#5648E6" },
          }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ColumnCustomizationModal = ({
  open,
  onClose,
  collections = [],
  columns = [],
  activeCollectionId,
  selectedColumnKeys = [],
  onApply,
  onSave,
  onSaveAs,
  onRenameView,
  onDeleteView,
  title = "Customize Columns",
  saving = false,
}) => {
  const [draftCollectionId, setDraftCollectionId] = useState(activeCollectionId);
  const [draftKeys, setDraftKeys] = useState(selectedColumnKeys);
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraftCollectionId(activeCollectionId);
    setDraftKeys([...(selectedColumnKeys || [])]);
    setSearch("");
    setSaveAsOpen(false);
    setRenameTarget(null);
    setDeleteTarget(null);
  }, [open, activeCollectionId, selectedColumnKeys]);

  const columnByKey = useMemo(
    () => new Map((columns || []).map((column) => [column.key, column])),
    [columns]
  );

  const standardCollections = collections.filter(
    (collection) => collection.section !== "custom"
  );
  const customCollections = collections.filter(
    (collection) => collection.section === "custom"
  );

  const activeCollection = collections.find(
    (collection) => collection.id === draftCollectionId
  );
  const canSave =
    activeCollection?.section === "custom" && Boolean(onSave);

  const filteredColumns = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return columns;
    return columns.filter((column) =>
      `${column.label || column.shortTitle || column.title || ""}`
        .toLowerCase()
        .includes(query)
    );
  }, [columns, search]);

  const groupedColumns = useMemo(
    () => groupColumns(filteredColumns),
    [filteredColumns]
  );

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      groupedColumns.forEach((group, index) => {
        const shouldExpand = index === 0 || Boolean(search.trim());
        if (next[group.label] === undefined) {
          next[group.label] = shouldExpand;
          changed = true;
        } else if (search.trim() && !next[group.label]) {
          next[group.label] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groupedColumns, search]);

  const selectedColumns = draftKeys
    .map((key) => columnByKey.get(key))
    .filter(Boolean);

  const syncCollectionFromKeys = (nextKeys) => {
    const current = collections.find((item) => item.id === draftCollectionId);
    // Keep named custom views selected while editing so Save can overwrite them.
    if (current?.section === "custom" && !current.isDefault) {
      return;
    }
    setDraftCollectionId(resolveMatchingCollectionId(collections, nextKeys));
  };

  const handleSelectCollection = (collection) => {
    const nextKeys = [...(collection.columnKeys || [])];
    setDraftKeys(nextKeys);
    setDraftCollectionId(collection.id);
  };

  const handleToggleColumn = (key) => {
    const nextKeys = draftKeys.includes(key)
      ? draftKeys.filter((item) => item !== key)
      : [...draftKeys, key];
    setDraftKeys(nextKeys);
    syncCollectionFromKeys(nextKeys);
  };

  const handleRemoveColumn = (key) => {
    const nextKeys = draftKeys.filter((item) => item !== key);
    setDraftKeys(nextKeys);
    syncCollectionFromKeys(nextKeys);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const nextKeys = Array.from(draftKeys);
    const [moved] = nextKeys.splice(result.source.index, 1);
    nextKeys.splice(result.destination.index, 0, moved);
    setDraftKeys(nextKeys);
    syncCollectionFromKeys(nextKeys);
  };

  const emitSelection = () => ({
    collectionId: draftCollectionId,
    selectedColumnKeys: draftKeys,
    collection: collections.find((item) => item.id === draftCollectionId),
  });

  const handleApply = () => {
    if (!draftKeys.length) return;
    onApply?.(emitSelection());
    onClose?.();
  };

  const handleSave = async () => {
    if (!draftKeys.length || !canSave) return;
    await onSave?.(emitSelection());
    onClose?.();
  };

  const handleSaveAsConfirm = async (name) => {
    if (!draftKeys.length) return;
    await onSaveAs?.({ ...emitSelection(), name });
    setSaveAsOpen(false);
    onClose?.();
  };

  const handleRenameConfirm = async (name) => {
    if (!renameTarget) return;
    await onRenameView?.({ collection: renameTarget, name });
    setRenameTarget(null);
  };

  const handleDeleteRequest = (collection) => {
    if (!collection || collection.isDefault || collection.id === DEFAULT_CUSTOM_VIEW_ID) {
      return;
    }
    setDeleteTarget(collection);
  };

  const handleDeleteConfirm = async () => {
    const collection = deleteTarget;
    if (!collection || collection.isDefault || collection.id === DEFAULT_CUSTOM_VIEW_ID) {
      setDeleteTarget(null);
      return;
    }
    await onDeleteView?.({ collection });
    if (draftCollectionId === collection.id) {
      const fallback =
        customCollections.find((item) => item.id === DEFAULT_CUSTOM_VIEW_ID) ||
        standardCollections[0];
      if (fallback) {
        setDraftCollectionId(fallback.id);
        setDraftKeys([...(fallback.columnKeys || [])]);
      }
    }
    setDeleteTarget(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 980,
            maxWidth: "94vw",
            height: 640,
            maxHeight: "86vh",
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #EEEAF5" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: ACCENT_SOFT,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TuneOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1F1A2B" }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#8A8699" }}>
                Choose a view, then refine and reorder metrics
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" disabled={saving}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>

        <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden" }}>
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              bgcolor: PANEL_BG,
              borderRight: "1px solid #EEEAF5",
              py: 2,
              overflowY: "auto",
            }}
          >
            <CollectionList
              title="STANDARD COLLECTION"
              collections={standardCollections}
              activeId={draftCollectionId}
              onSelect={handleSelectCollection}
            />
            <CollectionList
              title="MY COLLECTION"
              collections={customCollections}
              activeId={draftCollectionId}
              onSelect={handleSelectCollection}
              onRename={setRenameTarget}
              onDelete={handleDeleteRequest}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: 2,
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #EEEAF5",
            }}
          >
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search metrics..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#8A8699" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: 13,
                  bgcolor: "#FAFAFC",
                },
              }}
            />
            <Box sx={{ overflowY: "auto", pr: 0.5 }}>
              {groupedColumns.length === 0 ? (
                <Typography
                  sx={{ fontSize: 13, color: "#8A8699", py: 4, textAlign: "center" }}
                >
                  No metrics match your search
                </Typography>
              ) : (
                groupedColumns.map((group) => {
                  const expanded = expandedGroups[group.label] ?? true;
                  return (
                    <Box key={group.label} sx={{ mb: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            [group.label]: !expanded,
                          }))
                        }
                        sx={{
                          px: 1,
                          py: 0.75,
                          cursor: "pointer",
                          borderRadius: 1.5,
                          "&:hover": { bgcolor: "#F7F6FF" },
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 700, color: "#2F2B3A" }}
                        >
                          {group.label} ({group.items.length})
                        </Typography>
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 20,
                            color: "#8A8699",
                            transform: expanded ? "rotate(180deg)" : "none",
                            transition: "transform 0.15s ease",
                          }}
                        />
                      </Stack>
                      {expanded &&
                        group.items.map((column) => {
                          const checked = draftKeys.includes(column.key);
                          return (
                            <Stack
                              key={column.key}
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              sx={{
                                px: 0.5,
                                py: 0.15,
                                borderRadius: 1,
                                "&:hover": { bgcolor: "#F7F6FF" },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() => handleToggleColumn(column.key)}
                                sx={{
                                  color: "#C9C4D8",
                                  "&.Mui-checked": { color: ACCENT },
                                }}
                              />
                              <Typography sx={{ fontSize: 13, color: "#2F2B3A" }}>
                                {column.label || column.shortTitle || column.title}
                              </Typography>
                            </Stack>
                          );
                        })}
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>

          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              bgcolor: "#FAFAFC",
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: "#8A8699",
                }}
              >
                SELECTED COLUMNS
              </Typography>
              <Typography sx={{ fontSize: 11, color: ACCENT, fontWeight: 600 }}>
                {selectedColumns.length}
              </Typography>
            </Stack>
            <Box sx={{ overflowY: "auto", flex: 1, pr: 0.5 }}>
              {selectedColumns.length === 0 ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 6, px: 2 }}
                >
                  <ViewColumnOutlinedIcon
                    sx={{ fontSize: 28, color: "#C9C4D8", mb: 1 }}
                  />
                  <Typography
                    sx={{ fontSize: 13, color: "#8A8699", textAlign: "center" }}
                  >
                    Select metrics from the list to build this view
                  </Typography>
                </Stack>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="selected-columns">
                    {(provided) => (
                      <Stack
                        spacing={1}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {selectedColumns.map((column, index) => (
                          <Draggable
                            key={column.key}
                            draggableId={String(column.key)}
                            index={index}
                          >
                            {(dragProvided, snapshot) => (
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                sx={{
                                  bgcolor: "#FFFFFF",
                                  border: "1px solid",
                                  borderColor: snapshot.isDragging
                                    ? ACCENT
                                    : "#EEEAF5",
                                  borderRadius: 2,
                                  px: 1,
                                  py: 0.85,
                                  boxShadow: snapshot.isDragging
                                    ? "0 8px 20px rgba(103, 89, 255, 0.16)"
                                    : "0 1px 2px rgba(31, 26, 43, 0.04)",
                                }}
                              >
                                <Box
                                  {...dragProvided.dragHandleProps}
                                  sx={{
                                    display: "flex",
                                    color: "#B8B3C6",
                                    cursor: "grab",
                                  }}
                                >
                                  <DragIndicatorIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Typography
                                  sx={{
                                    flex: 1,
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    color: "#2F2B3A",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {column.label ||
                                    column.shortTitle ||
                                    column.title}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveColumn(column.key)}
                                  sx={{ p: 0.25 }}
                                >
                                  <CloseIcon
                                    sx={{ fontSize: 14, color: "#8A8699" }}
                                  />
                                </IconButton>
                              </Stack>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Box>
          </Box>
        </DialogContent>

        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.75 }}
        >
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{ textTransform: "none", color: "#5B5668", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Stack direction="row" spacing={1.25}>
            {onSaveAs && (
              <Button
                onClick={() => setSaveAsOpen(true)}
                disabled={!draftKeys.length || saving}
                sx={{ textTransform: "none", color: ACCENT, fontWeight: 700 }}
              >
                Save as…
              </Button>
            )}
            {canSave && (
              <Button
                variant="outlined"
                onClick={handleSave}
                disabled={!draftKeys.length || saving}
                sx={{
                  textTransform: "none",
                  borderColor: ACCENT,
                  color: ACCENT,
                  fontWeight: 700,
                  borderRadius: 2,
                  "&:hover": {
                    borderColor: "#5648E6",
                    bgcolor: ACCENT_SOFT,
                  },
                }}
              >
                {saving ? <CircularProgress size={18} /> : "Save"}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!draftKeys.length || saving}
              sx={{
                textTransform: "none",
                bgcolor: ACCENT,
                px: 2.5,
                borderRadius: 2,
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { bgcolor: "#5648E6", boxShadow: "none" },
              }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Dialog>

      <NamePromptDialog
        open={saveAsOpen}
        title="Save as new view"
        label="View name"
        confirmLabel="Save"
        loading={saving}
        onClose={() => setSaveAsOpen(false)}
        onConfirm={handleSaveAsConfirm}
      />

      <NamePromptDialog
        open={Boolean(renameTarget)}
        title="Rename view"
        label="View name"
        initialValue={renameTarget?.label || ""}
        confirmLabel="Rename"
        loading={saving}
        onClose={() => setRenameTarget(null)}
        onConfirm={handleRenameConfirm}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={saving ? undefined : () => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Delete view
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#2F2B3A", pt: 0.5 }}>
            Delete{" "}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {deleteTarget?.label || "this view"}
            </Typography>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={handleDeleteConfirm}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ColumnCustomizationModal;
